import { NetworkStatus, useLazyQuery, useMutation } from '@apollo/client';
import { Box, ToastAnimateFrom, ToastContent, ToastPosition, ToastType } from '@bechtel/gds';
import {
  FeedbackEquipmentInput,
  FeedbackEquipmentResult,
  MetadataColumn,
  UiMessageKey,
  UiTextEnum,
} from '@bechtel/sr-types';
import { Formik } from 'formik';
import { get, omit } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { boolean, object, string } from 'yup';
import { FormSkeleton, useLanguage, useTableMetadataContext } from '~/components';
import MessageModal from '~/components/organisms/message-modal/message';
import { FEEDBACK_EQUIPMENT, SEARCH_EQUIPMENT } from '~/gql';
import { usePermissions, useToast } from '~/hooks';
import { getDescription } from '~/utilities';
import { RightPanelStyled } from '~/views/commodities/styled';
import { AdditionalInfo } from './additional-info';
import EquipmentFeedbackForm from './form';
import { EquipmentFeedbackFormikState } from './form/types';
import { equipmentFeedbackInitialValues, getFormikData } from './utils';

const getYupSchema = (
  isAlternateLanguageUsed,
  getMultiLingualTextByKey,
  data: MetadataColumn[],
  permission: boolean = false,
  entityText: string = ''
) => {
  const constStatusDescription = getDescription(data, isAlternateLanguageUsed, 'constructionStatus');
  const tagDescription = getDescription(data, isAlternateLanguageUsed, 'tag');
  const constructionStatus = permission
    ? string()
        .required(getMultiLingualTextByKey(UiTextEnum.VALUE_CANNOT_BE_BLANK, [constStatusDescription]))
        .nullable()
    : string();

  return {
    tagInvalid: boolean(),
    tag: string()
      .required(getMultiLingualTextByKey(UiTextEnum.VALUE_CANNOT_BE_BLANK, [tagDescription]))
      .test(
        'tag-check',
        getMultiLingualTextByKey(UiTextEnum.VALUE_IS_NOT_VALID, [getMultiLingualTextByKey(entityText)]),
        function () {
          const { tagInvalid } = this.parent;
          return tagInvalid === false;
        }
      ),
    constructionStatus,
  };
};
const Equipments = () => {
  const { showToast } = useToast();
  const tableMetadata = useTableMetadataContext();
  const [initialValues, setInitialValues] = useState<EquipmentFeedbackFormikState>(equipmentFeedbackInitialValues);
  const { getMultiLingualTextByKey, isAlternateLanguageUsed } = useLanguage();
  const [searchEquipments, { loading, networkStatus, data: searchEquipmentsData, error }] = useLazyQuery(
    SEARCH_EQUIPMENT,
    {
      notifyOnNetworkStatusChange: true,
    }
  );
  const { canUpdateEquipmentFeedback } = usePermissions();

  const initialValueKeys = useRef(initialValues && Object?.keys(initialValues));
  const equipmentData = get(searchEquipmentsData, 'searchEquipments.items[0]');
  const entityText = getMultiLingualTextByKey('Equipment');

  const formikData = useMemo(() => {
    if (!equipmentData) return;
    return getFormikData(equipmentData, initialValueKeys.current);
  }, [equipmentData]);

  useEffect(() => {
    setInitialValues({ ...equipmentFeedbackInitialValues, ...formikData });
  }, [formikData]);

  const [feedbackEquipment, { data: feedbackEquipmentData, error: feedbackEquipmentError }] = useMutation<
    { feedbackEquipment: FeedbackEquipmentResult },
    { feedbackEquipmentInput: FeedbackEquipmentInput }
  >(FEEDBACK_EQUIPMENT, {
    onCompleted: (data) => {
      if (feedbackEquipmentError === undefined && data.feedbackEquipment.errors == null) {
        const feedbackData = get(data, 'feedbackEquipment');
        showToast(
          UiTextEnum.SAVED_SUCCESSFULLY_TOAST,
          [entityText, feedbackData?.equipment?.tag ?? ''],
          ToastType.INFO
        );
      }
    },
    onError: () => {
      showToast(UiTextEnum.ERROR_WHILE_UPDATING_TOAST, [entityText, ''], ToastType.WARNING);
    },
  });

  const validationSchema = object().shape(
    tableMetadata?.metadataColumns
      ? getYupSchema(
          isAlternateLanguageUsed,
          getMultiLingualTextByKey,
          tableMetadata?.metadataColumns,
          canUpdateEquipmentFeedback,
          entityText
        )
      : {}
  );

  const saveHandler = async (values) => {
    const id = searchEquipmentsData.searchEquipments.items[0].id;
    await feedbackEquipment({
      variables: {
        feedbackEquipmentInput: { ...values, id },
      },
    });
  };

  const errorKeys: UiMessageKey[] =
    get(feedbackEquipmentData, 'feedbackEquipment.infos') ??
    get(feedbackEquipmentData, 'feedbackEquipment.errors') ??
    [];

  return (
    <>
      <Formik
        enableReinitialize={true}
        validateOnChange={true}
        validateOnBlur={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          // Convert empty strings to null
          // @ts-ignore empy objec literal populated with values
          const sanitizedValues = Object.keys(values).reduce((acc, key) => {
            acc[key] = values[key] === '' ? null : values[key];
            return acc;
          }, {});
          const updatedValues = omit(sanitizedValues, ['tag', 'tagInvalid']);
          await saveHandler(updatedValues);
          setSubmitting(false);
        }}
      >
        <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
          {!tableMetadata ? (
            <FormSkeleton columns={1} rows={8} />
          ) : (
            <EquipmentFeedbackForm
              searchEquipments={searchEquipments}
              loading={loading || networkStatus === NetworkStatus.refetch}
            />
          )}
        </Box>
      </Formik>
      {errorKeys && errorKeys.length > 0 && <MessageModal messageKeys={errorKeys}></MessageModal>}
      <ToastContent position={ToastPosition.MIDDLE} animateFrom={ToastAnimateFrom.BOTTOM} />
      <RightPanelStyled isCollapsed={true}>
        <AdditionalInfo
          data={equipmentData}
          loading={loading || networkStatus === NetworkStatus.refetch}
          error={error}
        />
      </RightPanelStyled>
    </>
  );
};

export default Equipments;
